/**
 * Business time on the client.
 *
 * The organization has one business time zone (an IANA name, set under
 * Account). The API reports it with the user's access. Three kinds of time
 * value reach the UI, and each has one rule:
 *
 * - Instants: ISO strings with `Z` or an offset, Date objects, epoch numbers.
 *   Shown in the business time zone (the device zone until one is set).
 * - Wall-clock values: naive ISO date-times from view data and ontology
 *   records. They are already business-local and are shown as-is.
 * - Calendar dates: `YYYY-MM-DD`. Never shifted.
 *
 * When the business zone differs from the device zone, times carry a zone
 * label so nobody reads business time as their own.
 */

const UTC = 'UTC';
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?$/;
const INSTANT = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?(?:Z|[+-]\d{2}(?::?\d{2})?)$/i;
const TIME_PARTS = ['hour', 'minute', 'second', 'dayPeriod', 'fractionalSecondDigits'];

let snapshot = Object.freeze({ timeZone: null, isSet: false });
const listeners = new Set();

export const deviceTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || UTC;
  } catch {
    return UTC;
  }
};

export const isValidTimeZone = (zone) => {
  if (!zone || typeof zone !== 'string') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
};

/** Every IANA zone the browser knows, device zone first. */
export const timeZoneOptions = () => {
  let zones = [];
  try {
    zones = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
  } catch {
    zones = [];
  }
  return [...new Set([deviceTimeZone(), UTC, ...zones])];
};

/** Record the organization's business time zone (from the user's access). */
export const setBusinessTimeZone = (timeZone, isSet = Boolean(timeZone)) => {
  const zone = isSet && isValidTimeZone(timeZone) ? timeZone : null;
  if (zone === snapshot.timeZone && Boolean(zone) === snapshot.isSet) return;
  snapshot = Object.freeze({ timeZone: zone, isSet: Boolean(zone) });
  listeners.forEach((listener) => listener());
};

export const getBusinessTimeSnapshot = () => snapshot;

export const subscribeToBusinessTime = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** The zone instants are shown in. */
export const displayTimeZone = () => snapshot.timeZone || deviceTimeZone();

/** 'instant', 'wall', 'date', or null for values that are not times. */
export const timeValueKind = (value) => {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : 'instant';
  if (typeof value === 'number') return Number.isFinite(value) ? 'instant' : null;
  const text = String(value).trim();
  const dateOnly = text.match(DATE_ONLY);
  if (dateOnly) return isRealDateTime(dateOnly) ? 'date' : null;
  const wall = text.match(WALL_CLOCK);
  if (wall) return isRealDateTime(wall) ? 'wall' : null;
  const instant = text.match(INSTANT);
  if (instant) return isRealDateTime(instant) && !Number.isNaN(Date.parse(text)) ? 'instant' : null;
  return null;
};

/** Whether matched date/time parts name a real moment (no 30 February, no hour 25). */
function isRealDateTime(match) {
  const [, year, month, day, hour = '0', minute = '0', second = '0'] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second)));
  return (
    date.getUTCFullYear() === Number(year)
    && date.getUTCMonth() === Number(month) - 1
    && date.getUTCDate() === Number(day)
    && date.getUTCHours() === Number(hour)
    && date.getUTCMinutes() === Number(minute)
    && date.getUTCSeconds() === Number(second)
  );
}

const partsAsUtcDate = (match) => {
  const [, year, month, day, hour = '0', minute = '0', second = '0', fraction = '0'] = match;
  const millis = Number(String(fraction).padEnd(3, '0').slice(0, 3));
  return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second), millis));
};

const withoutTimeParts = (options) => {
  const next = { ...options };
  TIME_PARTS.forEach((key) => delete next[key]);
  delete next.timeStyle;
  delete next.timeZoneName;
  if (!Object.keys(next).some((key) => ['year', 'month', 'day', 'weekday', 'dateStyle'].includes(key))) {
    next.dateStyle = 'medium';
  }
  return next;
};

const hasTimeParts = (options) =>
  Boolean(options.timeStyle) || TIME_PARTS.some((key) => key in options);

/** Short name of a zone at an instant, for example "CDT". */
export const zoneAbbreviation = (zone = displayTimeZone(), at = new Date()) => {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' })
      .formatToParts(at)
      .find((item) => item.type === 'timeZoneName');
    return part?.value || zone;
  } catch {
    return zone;
  }
};

/** Whether times should say which zone they are in. */
export const needsZoneLabel = () => Boolean(snapshot.timeZone) && snapshot.timeZone !== deviceTimeZone();

const DEFAULT_DATE_TIME = { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' };

/**
 * Format any time value by its kind. `options` are Intl.DateTimeFormat
 * options; `label` is 'auto' (zone label when business and device zones
 * differ), true or false.
 */
export const formatTime = (value, options = DEFAULT_DATE_TIME, { locale, label = 'auto', fallback = '' } = {}) => {
  const kind = timeValueKind(value);
  if (!kind) return value == null || value === '' ? fallback : String(value);
  const wantsTime = hasTimeParts(options);
  const showLabel = wantsTime && kind !== 'date' && (label === true || (label === 'auto' && needsZoneLabel()));
  try {
    if (kind === 'instant') {
      const date = value instanceof Date ? value : new Date(value);
      const zone = displayTimeZone();
      const formatted = new Intl.DateTimeFormat(locale, { ...options, timeZone: zone }).format(date);
      return showLabel ? `${formatted} ${zoneAbbreviation(zone, date)}` : formatted;
    }
    const text = String(value).trim();
    const date = partsAsUtcDate(kind === 'date' ? text.match(DATE_ONLY) : text.match(WALL_CLOCK));
    const effective = kind === 'date' ? withoutTimeParts(options) : options;
    const formatted = new Intl.DateTimeFormat(locale, { ...effective, timeZone: UTC }).format(date);
    return showLabel ? `${formatted} ${zoneAbbreviation(displayTimeZone(), date)}` : formatted;
  } catch {
    return String(value);
  }
};

export const formatDateTime = (value, options = { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }, settings) =>
  formatTime(value, options, settings);

export const formatDate = (value, options = { month: 'short', day: 'numeric', year: 'numeric' }, settings) =>
  formatTime(value, { ...options }, settings);

export const formatClock = (value, options = { hour: 'numeric', minute: '2-digit' }, settings) =>
  formatTime(value, options, settings);

/** Business-zone calendar parts of an instant. */
export const businessParts = (value = new Date(), zone = displayTimeZone()) => {
  const date = value instanceof Date ? value : new Date(value);
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    })
      .formatToParts(date)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value]),
  );
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
    second: Number(parts.second),
  };
};

/** Today's calendar date in the business time zone, as YYYY-MM-DD. */
export const businessToday = (now = new Date()) => {
  const { year, month, day } = businessParts(now);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

/**
 * The business wall clock now, as a Date whose local fields hold it. For
 * renderers that do wall-clock arithmetic on local Dates built from view data.
 */
export const businessWallClockNow = (now = new Date()) => {
  const { year, month, day, hour, minute, second } = businessParts(now);
  return new Date(year, month - 1, day, hour, minute, second, now.getMilliseconds());
};

/**
 * The UTC instant (ISO string) of a wall-clock time in `zone`, for example a
 * `datetime-local` input read in the business time zone. Null when unparsable.
 */
export const wallClockToInstant = (value, zone = displayTimeZone()) => {
  const match = String(value || '').trim().match(WALL_CLOCK);
  if (!match) return null;
  const target = partsAsUtcDate(match).getTime();
  let guess = target;
  // Two corrections settle the zone offset, including across DST changes.
  for (let step = 0; step < 2; step += 1) {
    const shown = businessParts(new Date(guess), zone);
    guess += target - Date.UTC(shown.year, shown.month - 1, shown.day, shown.hour, shown.minute, shown.second);
  }
  return new Date(guess).toISOString();
};

/**
 * The instant a time value refers to, as a Date: wall-clock values and
 * calendar dates (midnight) are read in the business time zone. Null for
 * values that are not times.
 */
export const toInstantDate = (value) => {
  const kind = timeValueKind(value);
  if (kind === 'instant') return value instanceof Date ? value : new Date(value);
  if (kind === 'wall') return new Date(wallClockToInstant(value));
  if (kind === 'date') return new Date(wallClockToInstant(`${String(value).trim()}T00:00`));
  return null;
};

/** Naive business-local ISO string for an instant, as ontology values carry it. */
export const toBusinessWallClockIso = (value = new Date()) => {
  const { year, month, day, hour, minute, second } = businessParts(value);
  const pad = (number) => String(number).padStart(2, '0');
  return `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}:${pad(second)}`;
};
